import { useCallback, useRef } from 'react'

import { EmailCaptureV2ArmType, EmailCaptureV2Description, EmailCaptureV2Metric } from './emailCaptureExperimentV2Types'
import { LD_CUSTOM_EVENTS } from '../../common/constants/constants'
import { COMMON_MIXPANEL_PAGE, COMMON_MIXPANEL_PROPERTIES } from '../../common/constants/mixpanelConstants'
import { PRODUCT_TYPE } from '../../common/constants/productType'
import { ExperimentPayload } from '../types'
import { generateEmailCapturePageExperimentV2Payload } from '../utils'

interface useEmailCaptureExperimentV2Args {
  emailCapturePageExperimentV2: string
  deviceUUID: string
  customerName: string
  productType: PRODUCT_TYPE
  postExperimentCallback?: (payload: ExperimentPayload) => void
  postLDCallback?: (event: LD_CUSTOM_EVENTS, payload: ExperimentPayload) => void
  postMixpanelCallback?: (event: string, eventData: { [key: string]: any }) => void
  logToSumo?: (message: string) => void
}

export const useEmailCaptureExperimentV2 = ({
  emailCapturePageExperimentV2,
  deviceUUID,
  customerName,
  productType,
  postExperimentCallback,
  postLDCallback,
  postMixpanelCallback,
  logToSumo,
}: useEmailCaptureExperimentV2Args) => {
  let arm: EmailCaptureV2ArmType = EmailCaptureV2ArmType.CONTROL
  if (emailCapturePageExperimentV2 === 'Experiment A') {
    arm = EmailCaptureV2ArmType.EXPERIMENT_A
  } else if (emailCapturePageExperimentV2 === 'Experiment B') {
    arm = EmailCaptureV2ArmType.EXPERIMENT_B
  }

  // Keep track of which metrics have been posted
  const metricsPosted = useRef<Record<EmailCaptureV2Metric, boolean>>(
    Object.fromEntries(Object.values(EmailCaptureV2Metric).map((metric) => [metric, false])) as Record<
      EmailCaptureV2Metric,
      boolean
    >,
  )

  const submitExperimentPayload = useCallback(
    ({
      metric,
      description,
      launchDarklyEvent,
      mixpanelEvent,
    }: {
      metric?: EmailCaptureV2Metric
      description?: EmailCaptureV2Description
      launchDarklyEvent?: LD_CUSTOM_EVENTS
      mixpanelEvent?: string
    }) => {
      // Early return if metric already posted
      if (metric && metricsPosted.current[metric]) {
        return
      }

      const isPayloadReady = arm && deviceUUID && customerName

      if (isPayloadReady && metric && description) {
        const experimentPayload = generateEmailCapturePageExperimentV2Payload({
          arm,
          deviceUUID,
          customerName,
          productType,
          description,
          metric,
        })

        // 'Click continue' events are not needed for the DS pipeline
        if (metric === EmailCaptureV2Metric.VIEW_PAGE && postExperimentCallback) {
          try {
            postExperimentCallback(experimentPayload)
            metricsPosted.current[metric] = true
          } catch (error) {
            const errorMsg = 'Unable to post to experiment endpoint for email capture experiment v2'
            console.error(errorMsg)
            logToSumo?.(`${errorMsg} - ${error}`)
          }
        }

        if (launchDarklyEvent && postLDCallback) {
          try {
            postLDCallback(launchDarklyEvent, experimentPayload)
            metricsPosted.current[metric] = true
          } catch (error) {
            const errorMsg = 'Unable to post to launch darkly endpoint for email capture experiment v2'
            console.error(errorMsg)
            logToSumo?.(`${errorMsg} - ${error}`)
          }
        }

        if (mixpanelEvent && postMixpanelCallback) {
          try {
            postMixpanelCallback(mixpanelEvent, {
              arm,
              deviceUUID,
              [COMMON_MIXPANEL_PROPERTIES.PAGE]: COMMON_MIXPANEL_PAGE.REGISTER_BY_EMAIL,
            })
            metricsPosted.current[metric] = true
          } catch (error) {
            const errorMsg = 'Unable to post to Mixpanel endpoint for email capture experiment v2'
            console.error(errorMsg)
            logToSumo?.(`${errorMsg} - ${error}`)
          }
        }
      }
    },
    [
      arm,
      deviceUUID,
      customerName,
      productType,
      postExperimentCallback,
      postLDCallback,
      postMixpanelCallback,
      logToSumo,
    ],
  )

  return { submitExperimentPayload, arm }
}
